/**
 * GalleryRenderer - Renders tiles in gallery mode
 *
 * Gallery mode displays all tiles in a single continuous grid that flows
 * across the configured number of columns. No row headers or separators.
 */

import {MediaCardType} from '@wandb/weave/common/types/media';
import {clsx} from 'clsx';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Card} from 'semantic-ui-react';

import {CustomRunNames} from '../../../../state/views/customRunNames/types';
import * as PanelTypes from '../../../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../../../state/views/types';
import {MediaConfigWithDefaults} from '../../Component';
import {TilePagination} from '../../tiles';
import {getMaskOptions} from '../../tiles/getTilesWithMaskOptions';
import {TileFooterSlot, TileHeaderSlot} from '../slots';
import {GridCell, GridData, SlotRegistry} from '../types';
import {TileRenderer} from './TileRenderer';

export type GalleryRendererProps = {
  gridData: GridData;
  slots: SlotRegistry;
  config: MediaConfigWithDefaults;
  cardWidth: number;
  cardHeight: number;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
  columnCount: number;
  shrunk?: boolean;
  showActualSize?: boolean;
  allMasks?: any;
  tilePagination?: TilePagination | null;
};

/**
 * Renders tiles in gallery mode - all tiles in one continuous grid.
 *
 * @param props - GalleryRendererProps
 * @returns React element
 */
export const GalleryRenderer: React.FC<GalleryRendererProps> = ({
  gridData,
  slots,
  config,
  cardWidth,
  cardHeight,
  mediaType,
  mediaSliderKey,
  panelRef,
  disableRunLinks,
  customRunNames,
  columnCount,
  shrunk,
  showActualSize,
  allMasks,
  tilePagination,
}) => {
  // Helper to expand cells with mask options into multiple tiles
  const expandCellsWithMasks = (cells: GridCell[]): GridCell[] => {
    if (!allMasks) {
      return cells;
    }

    const expandedCells: GridCell[] = [];
    for (const cell of cells) {
      if (cell.isEmpty) {
        expandedCells.push(cell);
        continue;
      }

      const maskOptionsList = getMaskOptions(config, allMasks, {
        run: cell.run,
        mediaStep: cell.step,
        mediaIndex: cell.mediaIndex,
        mediaKey: cell.mediaKey,
        runSignature: cell.tileSpec.runSignature,
      });

      // Create a cell for each mask option
      for (const maskOpts of maskOptionsList) {
        expandedCells.push({
          ...cell,
          maskOptions: maskOpts,
        });
      }
    }

    return expandedCells;
  };

  const tileHeaderControls = slots['tile-header'] || [];
  const tileFooterControls = slots['tile-footer'] || [];

  // Flatten all cells into a single array for gallery display
  const flatCells = gridData.rows.flatMap(row => row.cells);

  // Apply pagination if provided
  const paginatedCells = tilePagination
    ? flatCells.slice(
        tilePagination.start,
        tilePagination.start + tilePagination.size
      )
    : flatCells;

  // Expand cells with mask options
  const allCells = expandCellsWithMasks(paginatedCells);

  return (
    <div className="flex w-full flex-col">
      <Card.Group
        style={{
          gridTemplateColumns: `repeat(${columnCount},${(
            100 / columnCount
          ).toFixed(2)}%)`,
          display: allCells.length > 0 ? 'grid' : 'flex',
          // justifyItems: 'center',
          margin: 0,
        }}
        className={clsx('media-cards w-full justify-between', {
          'media-cards--pixelated': config.pixelated,
          'media-cards--table': mediaType === 'table',
          'media-cards--shrunk': shrunk,
          'media-cards--actual': showActualSize,
          'media-cards--full': !showActualSize,
        })}>
        {allCells.map((cell, idx) => (
          <TileRenderer
            key={`tile-${idx}`}
            cell={cell}
            tileIndex={idx}
            cardWidth={cardWidth}
            cardHeight={cardHeight}
            config={config}
            mediaType={mediaType}
            mediaSliderKey={mediaSliderKey}
            panelRef={panelRef}
            disableRunLinks={disableRunLinks}
            customRunNames={customRunNames}
            tileHeaderControls={
              <TileHeaderSlot
                controls={tileHeaderControls}
                cell={cell}
                disableRunLinks={disableRunLinks}
              />
            }
            tileFooterControls={
              <TileFooterSlot controls={tileFooterControls} cell={cell} />
            }
          />
        ))}
      </Card.Group>
    </div>
  );
};
